import { Typography } from "@mui/joy";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DSTIRecord, DSTIResponseWrapper } from "../../../../models/DSTI/site-dsti-list.interface";
import { TabPanel, TabView } from "primereact/tabview";
import useApiService from "../../../../hooks/useApiService";
import { Card } from "primereact/card";
import styles from "../sitedsti.module.scss";
import { formatDateTime } from "../../../../utils/DateUtils";
import { RouteComponentProps } from "../../../../models/RouteComponent";
import TaskAndEquipment from "../components/TaskAndEquipment";
import DSTI from "../components/DSTI";
import Ppe from "../components/ppe";
import DSTIPhotos from "../components/DSTIPhotos";
import DSTIAttendance from "../components/DSTIAttendance";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { StatusBadge } from "../../../../components/StatusBadge/StatusBadge";
import { capitaliseEveryFirstLetter, keyToHuman } from "../../../../utils/generalUtils";
import { Dropdown } from "primereact/dropdown";
import { CircleWarning } from "../../../../assets/icons";
import { useModal } from "../../../../context/modalContext";
import ButtonPanel from "../../../../components/Shared/ButtonPanel";

function DSTIDetails(props: RouteComponentProps) {
  const [dstiDetails, setDstiDetails] = useState<DSTIResponseWrapper>();
  const [modalToShow, setModalToShow] = useState<"withdraw" | null>(null);
  const { showModal, hideModal } = useModal();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { loading, error } = useApiService<DSTIResponseWrapper>({
    method: "GET",
    url: `dsti-list/${id}`,
    apiName: "core",
    onSuccess: data => {
      if (data) {
        setDstiDetails(data);
      } else {
        console.error("DSTI not found");
      }
    },
  });

  const { loading: updatingDsti, fetchData: updateDsti } = useApiService<DSTIRecord>({
    fetchOnMount: false,
    method: "PUT",
    url: `dsti/${id}`,
    apiName: "core",
    onSuccess: data => {
      const updatedDSTI = {
        attendance: dstiDetails?.attendance,
        dsti: data,
      };
      setDstiDetails(updatedDSTI as DSTIResponseWrapper);
    },
  });

  useEffect(() => {
    props.setGlobalLoader(loading || updatingDsti);
  }, [loading, updatingDsti, props]);

  useEffect(() => {
    if (dstiDetails && dstiDetails.dsti.status === "not-started") {
      showModal(
        "Cannot update DSTI",
        <>
          <div style={{ margin: "1rem 0" }}>
            Note that you are not able to start or update the DSTI through the admin portal.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={hideModal} label={"OK"} color={"primary"} outlined={true} />
          </div>
        </>
      );
    }
  }, [dstiDetails?.dsti.status]);

  async function handleWithdraw() {
    let payload = dstiDetails?.dsti as DSTIRecord;

    payload = {
      ...payload,
      status: "withdrawn",
    };

    updateDsti({
      payload: payload,
    });

    navigate("/site-dsti");
  }

  function getStatusVariant(
    status: "not-started" | "abandoned" | "in-progress" | "skipped" | "withdrawn" | "completed"
  ): "error" | "success" | "warning" | "info" {
    switch (status) {
      case "in-progress":
        return "warning";
      case "completed":
        return "success";
      case "skipped":
      case "abandoned":
        return "error";
      case "withdrawn":
      case "not-started":
      default:
        return "info";
    }
  }

  const optionTemplate = (option: { label: string; value: string }) => {
    switch (option.value) {
      case "withdraw":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <CircleWarning />
            <div>{option.label}</div>
          </div>
        );
      default:
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <div>{option.label}</div>
          </div>
        );
    }
  };

  if (error) {
    return (
      <>
        <Typography level="h2" component="h1">
          Daily Site Task Instructions (DSTI)
        </Typography>
        <div className={"error-wrapper"}>
          <h2> Something went wrong, please try again later</h2>
        </div>
      </>
    );
  }

  return (
    <>
      <Typography level="h2" component="h1">
        Daily Site Task Instructions (DSTI)
      </Typography>
      {dstiDetails?.dsti && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <StatusBadge variant={getStatusVariant(dstiDetails?.dsti.status)}>
            {capitaliseEveryFirstLetter(keyToHuman(dstiDetails?.dsti.status))}
          </StatusBadge>
          {dstiDetails?.dsti.status !== "withdrawn" && (
            <Dropdown
              options={[{ label: "Withdraw DSTI", value: "withdraw" }]}
              dropdownIcon={"pi pi-cog"}
              placeholder=" "
              className="p-dropdown-icon-only"
              style={{
                border: "none",
                background: "transparent",
                boxShadow: "none",
                minWidth: "auto",
              }}
              showClear={false}
              onChange={val => {
                if (val.value === "withdraw") {
                  setModalToShow("withdraw");
                }
              }}
              itemTemplate={optionTemplate}
              panelStyle={{ minWidth: "350px" }}
            />
          )}
        </div>
      )}

      <TabView>
        <TabPanel header={"Site & Project Information"}>
          <Card title={"Site Information"}>
            <div className={styles["responsive-grid-2"]}>
              <div>
                <strong>Site Name:</strong> {dstiDetails?.dsti.site_name}
              </div>
              <div>
                <strong>Date created:</strong> {formatDateTime(dstiDetails?.dsti.created_at)}
              </div>
              <div>
                <strong>Responsible person:</strong> {dstiDetails?.dsti.employee_full}
              </div>
            </div>
          </Card>
        </TabPanel>
        <TabPanel header={"Task & Equipment"}>
          <TaskAndEquipment details={dstiDetails?.dsti} />
        </TabPanel>
        <TabPanel header={"DSTI"}>
          <DSTI dstiDetails={dstiDetails?.dsti} />
        </TabPanel>
        <TabPanel header={"PPE"}>
          <Ppe dstiDetails={dstiDetails?.dsti} />
        </TabPanel>
        <TabPanel header={"Photos"}>
          <DSTIPhotos dstiDetails={dstiDetails?.dsti} restProps={props} />
        </TabPanel>
        <TabPanel header={"Attendance"}>
          <DSTIAttendance dstiAttendance={dstiDetails?.attendance} />
        </TabPanel>
      </TabView>
      {modalToShow === "withdraw" && (
        <Dialog
          style={{ width: "30vw" }}
          onHide={() => setModalToShow(null)}
          visible={true}
          header={"Are you sure?"}
        >
          <div className={"my-2"}>
            This will withdraw the DSTI created at
            <strong>{formatDateTime(dstiDetails?.dsti.created_at)}</strong> and completed at
            <strong>{formatDateTime(dstiDetails?.dsti.updated_at)} </strong>
            by <strong>{dstiDetails?.dsti.employee_full}</strong>. The action cannot be undone.
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", margin: "1rem 0 0" }}>
            <Button
              onClick={() => {
                setModalToShow(null);
              }}
              label={"Go Back"}
              outlined={true}
            />
            <Button
              severity={"danger"}
              label={"Yes, Withdraw"}
              outlined={false}
              onClick={async () => {
                await handleWithdraw();
              }}
            />
          </div>
        </Dialog>
      )}
    </>
  );
}

export default DSTIDetails;
